import moment from "moment";
import {
  adPerformanceColumns,
  adPerformanceMockRows,
  isAdminReportRole,
} from "./adPerformanceReportData";

export const getAdReportColumns = (roleId) => {
  const isAdminView = isAdminReportRole(roleId);

  return adPerformanceColumns
    .filter((column) => isAdminView || !column.adminOnly)
    .map((column) => ({
      ...column,
      label:
        !isAdminView && column.distributorLabel
          ? column.distributorLabel
          : column.label,
    }));
};

export const getScopedAdReportRows = (roleId) => {
  if (isAdminReportRole(roleId)) {
    return adPerformanceMockRows;
  }

  return adPerformanceMockRows.filter(
    (row) => row.distributorName === "Brightline Benefits",
  );
};

export const filterRowsByDateRange = (rows, dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return [];
  }

  const from = moment(dateFrom).startOf("day");
  const to = moment(dateTo).endOf("day");

  // Only include records whose campaign start date falls within the selected
  // range. This keeps the table, KPI cards and charts strictly scoped to the
  // user's chosen period instead of showing campaigns that merely overlap it.
  return rows.filter((row) => {
    const start = moment(row.campaignStart).startOf("day");
    return start.isSameOrAfter(from) && start.isSameOrBefore(to);
  });
};

export const applyTableFilters = (rows, filters = {}) => {
  return rows.filter((row) =>
    ["position", "payment", "status"].every((field) => {
      const values = filters[field] || [];
      return values.length === 0 || values.includes(row[field]);
    }),
  );
};

const parseCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const numeric = Number(String(value).replaceAll(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

/* Percent values in the mock data share the currency parser's strip-and-
 * coerce behaviour (e.g. "12.5%" → 12.5), so we re-use it here rather than
 * duplicate the implementation. */
const parsePercent = parseCurrency;

// Distributes a campaign's total impressions/clicks evenly across the days that
// fall inside the selected date range so we can render meaningful trend lines
// from the existing per-row aggregates.
const distributeRowAcrossDays = (row, rangeStart, rangeEnd) => {
  const campaignStart = moment(row.campaignStart).startOf("day");
  const campaignEnd = moment(row.campaignEnd).startOf("day");
  const effectiveStart = moment.max(campaignStart, rangeStart);
  const effectiveEnd = moment.min(campaignEnd, rangeEnd);

  if (effectiveEnd.isBefore(effectiveStart)) {
    return [];
  }

  const totalDays = campaignEnd.diff(campaignStart, "days") + 1;
  const perDayImpressions = (row.impressions || 0) / totalDays;
  const perDayClicks = (row.clicks || 0) / totalDays;
  const days = [];
  const cursor = effectiveStart.clone();

  while (cursor.isSameOrBefore(effectiveEnd)) {
    days.push({
      date: cursor.format("YYYY-MM-DD"),
      impressions: perDayImpressions,
      clicks: perDayClicks,
    });
    cursor.add(1, "day");
  }

  return days;
};

// KPI cards, charts and the data table all share the same `rows` input so the
// dashboard widget always reflects exactly what the user sees in the table.
// When a date range is provided, the KPI totals are pro-rated to the portion
// of each campaign that actually falls inside the selected range so the cards
// stay consistent with the trend charts.
export const getAdPerformanceKpis = (
  rows = [],
  dateFrom = null,
  dateTo = null,
) => {
  const hasRange = Boolean(dateFrom && dateTo);
  const rangeStart = hasRange ? moment(dateFrom).startOf("day") : null;
  const rangeEnd = hasRange ? moment(dateTo).startOf("day") : null;

  let totalImpressions = 0;
  let totalClicks = 0;
  let totalSpend = 0;

  rows.forEach((row) => {
    const rowImpressions = row.impressions || 0;
    const rowClicks = row.clicks || 0;
    const rowSpend = parseCurrency(row.amountCharged);

    if (!hasRange) {
      totalImpressions += rowImpressions;
      totalClicks += rowClicks;
      totalSpend += rowSpend;
      return;
    }

    const campaignStart = moment(row.campaignStart).startOf("day");
    const campaignEnd = moment(row.campaignEnd).startOf("day");
    const effectiveStart = moment.max(campaignStart, rangeStart);
    const effectiveEnd = moment.min(campaignEnd, rangeEnd);

    if (effectiveEnd.isBefore(effectiveStart)) {
      return;
    }

    const campaignDays = campaignEnd.diff(campaignStart, "days") + 1;
    const inRangeDays = effectiveEnd.diff(effectiveStart, "days") + 1;
    const ratio = campaignDays > 0 ? inRangeDays / campaignDays : 0;

    totalImpressions += Math.round(rowImpressions * ratio);
    totalClicks += Math.round(rowClicks * ratio);
    totalSpend += rowSpend * ratio;
  });

  const averageCtr =
    totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  return {
    totalImpressions,
    totalClicks,
    totalSpend,
    averageCtr,
  };
};

export const getDailyTrendSeries = (rows, dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return [];
  }

  const rangeStart = moment(dateFrom).startOf("day");
  const rangeEnd = moment(dateTo).startOf("day");
  const buckets = new Map();
  const cursor = rangeStart.clone();

  while (cursor.isSameOrBefore(rangeEnd)) {
    const key = cursor.format("YYYY-MM-DD");
    buckets.set(key, { date: key, impressions: 0, clicks: 0 });
    cursor.add(1, "day");
  }

  rows.forEach((row) => {
    distributeRowAcrossDays(row, rangeStart, rangeEnd).forEach((day) => {
      const bucket = buckets.get(day.date);
      if (bucket) {
        bucket.impressions += day.impressions;
        bucket.clicks += day.clicks;
      }
    });
  });

  return Array.from(buckets.values()).map((bucket) => ({
    date: bucket.date,
    label: moment(bucket.date).format("MMM D"),
    impressions: Math.round(bucket.impressions),
    clicks: Math.round(bucket.clicks),
  }));
};

export const getPerformanceByAd = (rows = []) =>
  rows.map((row) => ({
    bannerName: row.bannerName,
    impressions: row.impressions || 0,
    clicks: row.clicks || 0,
  }));

export const getCtrByAd = (rows = []) =>
  rows.map((row) => ({
    bannerName: row.bannerName,
    ctr: parsePercent(row.ctr),
  }));

export const getImpressionsShareByAd = (rows = []) =>
  rows
    .map((row) => ({
      name: row.bannerName,
      value: row.impressions || 0,
    }))
    .filter((entry) => entry.value > 0);

export const getSpendShareByAd = (rows = []) =>
  rows
    .map((row) => ({
      name: row.bannerName,
      value: parseCurrency(row.amountCharged),
    }))
    .filter((entry) => entry.value > 0);

// Aggregates impressions and clicks per Target Device/OS so the dashboard can
// surface how the audience splits across Mobile, Tablet and Windows. Devices
// are normalised from the row's comma-separated `targetDevices` string.
const SUPPORTED_DEVICES = ["Mobile", "Tablet", "Windows"];

const normaliseDevices = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.split(",").map((device) => device.trim());
  }
  return [];
};

export const getPerformanceByDevice = (rows = []) => {
  const buckets = SUPPORTED_DEVICES.reduce((acc, device) => {
    acc[device] = { device, impressions: 0, clicks: 0 };
    return acc;
  }, {});

  rows.forEach((row) => {
    const devices = normaliseDevices(row.targetDevices).filter((device) =>
      SUPPORTED_DEVICES.includes(device),
    );
    if (devices.length === 0) {
      return;
    }
    devices.forEach((device) => {
      buckets[device].impressions += row.impressions || 0;
      buckets[device].clicks += row.clicks || 0;
    });
  });

  return SUPPORTED_DEVICES.map((device) => buckets[device]);
};

export const getImpressionsShareByDevice = (rows = []) =>
  getPerformanceByDevice(rows)
    .map(({ device, impressions }) => ({ name: device, value: impressions }))
    .filter((entry) => entry.value > 0);

export const createCsv = (rows, columns) => {
  const selectedColumns = columns.filter((column) => column.isSelected);
  const header = selectedColumns.map((column) => column.label).join(",");
  const body = rows.map((row) =>
    selectedColumns
      .map(
        (column) =>
          `"${String(row[column.fieldName] ?? "").replaceAll('"', '""')}"`,
      )
      .join(","),
  );

  return [header, ...body].join("\n");
};
